import { Toast } from '@base-ui/react/toast';
import { CloseCircle, Danger, Information, TickCircle } from 'iconsax-reactjs';
import { type FC, type ReactNode, useMemo } from 'react';
import { Button } from './Button';
import { cn } from './react';

type ToastVariant = 'negative' | 'positive' | 'info' | 'default';

const VARIANTS: Record<
  ToastVariant,
  {
    accent: string;
    icon: string;
    Icon: typeof Danger | null;
  }
> = {
  negative: {
    accent: 'bg-background-negative',
    icon: 'text-content-negative',
    Icon: Danger,
  },
  positive: {
    accent: 'bg-background-positive',
    icon: 'text-content-positive',
    Icon: TickCircle,
  },
  info: {
    accent: 'bg-background-notice',
    icon: 'text-content-notice',
    Icon: Information,
  },
  default: {
    accent: 'bg-border-disabled',
    icon: 'text-content-tertiary',
    Icon: null,
  },
};

const ToastList: FC = () => {
  const { toasts } = Toast.useToastManager();

  return toasts.slice(0, 6).map((toast) => {
    const variant = VARIANTS[(toast.type as ToastVariant) || 'default'];
    const Icon = variant.Icon;

    return (
      <Toast.Root
        key={toast.id}
        toast={toast}
        className={cn(
          'rounded-lg rounded-s-none border select-none overflow-hidden',
          'absolute left-0 bottom-0 w-full origin-bottom shadow-2xl',
          '[--gap:1rem]',
          '[--peek:1.5rem]',
          '[--scale:calc(max(0,1-(var(--toast-index)*0.05)))]',
          '[--offset-y:calc(var(--toast-index)*var(--gap)*-1)]',
          'translate-y-[calc(var(--offset-y)-(var(--toast-index)*var(--peek)))]',
          'transition-all duration-300 ease-in-out',
          'data-starting-style:translate-y-full',
          'data-ending-style:opacity-0',
          'bg-background-primary border-border-disabled',
        )}
      >
        <span
          className={cn(
            'absolute inset-y-0 start-0 w-[6px] pointer-events-none',
            variant.accent,
          )}
        />
        <Toast.Content className="flex items-start gap-2.5 p-3 ps-4">
          {Icon && (
            <Icon
              variant="Bold"
              size={20}
              className={cn('shrink-0 mt-px', variant.icon)}
            />
          )}
          <div className="grow min-w-0 overflow-hidden">
            <Toast.Title className="text-sm font-medium text-content-primary" />
            <Toast.Description className="text-sm mt-0.5 text-content-secondary" />
          </div>
          <Toast.Close
            className="shrink-0 -mt-1 -me-1"
            aria-label="Close"
            data-close
          >
            <Button
              variant="transparent"
              type="div"
              className="bg-transparent! text-content-tertiary"
              fab
              size={8}
            >
              <CloseCircle size={20} />
            </Button>
          </Toast.Close>
        </Toast.Content>
      </Toast.Root>
    );
  });
};

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => (
  <Toast.Provider limit={6}>
    {children}
    <Toast.Portal container={document.getElementById('toast-root')}>
      <Toast.Viewport className="fixed bottom-3 left-1/2 -translate-x-1/2 mx-auto flex flex-col-reverse gap-3 max-w-md w-[calc(100%-1rem)]">
        <ToastList />
      </Toast.Viewport>
    </Toast.Portal>
  </Toast.Provider>
);

export const useToast = () => {
  const toastManager = Toast.useToastManager();
  return useMemo(() => {
    return {
      add: ({
        title,
        message,
        variant,
        timeout = 3_000,
      }: {
        title?: string;
        message?: ReactNode;
        variant?: ToastVariant;
        timeout?: number;
      }) =>
        toastManager.add({
          title,
          description: message,
          type: variant && variant !== 'default' ? variant : undefined,
          timeout,
        }),
      remove: toastManager.close,
    };
  }, [toastManager.add, toastManager.close]);
};
